
/* eslint-disable @typescript-eslint/camelcase */
import { Vue, Options } from "vue-class-component";
import {
  formatInputNumberOnlyV2,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import SelectPhoneCountry from "../../shipment/booking/component/select-phone-country.vue";
import removeNewLineWhitespace from "@/app/infrastructures/misc/common-library/RemoveNewLineWhitespace";
import debounce from "lodash/debounce";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { CustomerManagementController } from "@/app/ui/controllers/CustomerManagementController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { Customer } from "@/domain/entities/Customer";
import { ApiStateData } from "@/domain/entities/Api";
import { countries } from "@/app/infrastructures/misc/BulkData";
import { Commodity } from "@/domain/entities/Commodity";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { RequestListCommodity } from "@/data/payload/api/CommodityApiRequest";
import { ValidateInputPhoneNumber } from "@/app/infrastructures/misc/common-library/ValidateInputPhoneNumber";
import { getPhoneDisplayCode } from '@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber';
import SavedPackageForm from "../component/saved-package-form.vue";
import SavedPackageRow from "../component/saved-package-row.vue";
import { SavedPackagePayload } from "@/data/payload/contracts/CustomerManagementResquest";

interface SavedPackageDraft {
  key: string;
  id?: number;
  isOpen: boolean;
  commodityCode: string;
  commodityName: string;
  isDangerousGoods: boolean;
  isQuarantine: boolean;
  totalPieces: number;
  totalGrossWeight: number;
  pieces: Array<{
    grossWeight: number;
    length: number;
    width: number;
    height: number;
  }>;
}

@Options({
  components: {
    SelectPhoneCountry,
    SavedPackageForm,
    SavedPackageRow
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    if (to.name === "login") {
      next();
    }
    if (this.hasChanged && !this.openSuccess) {
      this.onOpenClosePage(to);
      if (this.answer) {
        this.answer = false;
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  }
})
export default class EditManagementCustomer extends Vue {
  countryFiltered = countries;
  apiListData = new ApiStateData();
  customer: Customer = new Customer();
  id = 0;
  firstLanding = true;

  created() {
    this.phoneNumberCode = getPhoneDisplayCode(
      AccountController.accountData?.countryCode
    );
  }

  mounted(): void {
    this.id = Number(this.$route.query.id || 0);
    this.getCustomerDetail();
  }

  async getCustomerDetail() {
    this.apiListData.loading = true;
    try {
      const resp = await CustomerManagementController.getCustomerDetail(
        this.id
      );
      this.name = resp.customerName;
      this.extractPhoneDetails(resp.customerPhoneNumber, countries);
      if (resp.customerDistrictId != 0) {
        this.fetchDistrictId(resp.customerDistrictId);
      }
      if (resp.customerZipCode != "") {
        this.zipCode = {
          id: Number(resp.customerZipCode),
          name: resp.customerZipCode
        };
      }
      if (resp.customerCommodity.name) {
        this.getCommodityList();
      }
      if (resp.customerCommodity.name) {
        this.commodity = resp.customerCommodity;
      }
      this.email = resp.customerEmail;
      this.note = resp.customerNote;
      this.address = resp.customerAddress;
      this.addressType = resp.customerAddressType;
      this.apiListData.errorType = "";
    } catch (error) {
      this.apiListData.errorType = parsingErrorResponse(error).type;
    } finally {
      this.apiListData.loading = false;
      this.firstLanding = false;
    }
  }

  goBack() {
    this.$router.back();
  }

  get isNoteValid() {
    return this.note.length > 2 && this.note.length <= 200;
  }

  get title(): string {
    return this.$t("managementCustomer.editPageTitle");
  }

  get isFormValid() {
    return true;
  }

  isDisabled = true;

  activeTab = "alamat";
  savedPackageDrafts: SavedPackageDraft[] = [];
  deleteSavedPackageKey: string | null = null;

  get tabs() {
    return [
      { title: this.$t("managementCustomer.tab.alamat"), value: "alamat" },
      { title: this.$t("managementCustomer.tab.savedPackage"), value: "paket-tersimpan" }
    ];
  }

  onChangeTab(value: string) {
    this.activeTab = value;
    if (value === "paket-tersimpan" && !this.savedPackageDrafts.length) {
      this.prefillSavedPackages();
    }
  }

  async prefillSavedPackages() {
    try {
      const list = await CustomerManagementController.getSavedPackageList(
        this.id
      );
      for (const pkg of list) {
        let pieces = [] as SavedPackageDraft["pieces"];
        try {
          const detail = await CustomerManagementController.getSavedPackageDetail({
            id: this.id,
            packageId: pkg.id
          });
          pieces = detail.pieces.map((p) => ({
            grossWeight: p.grossWeight,
            length: p.length,
            width: p.width,
            height: p.height
          }));
        } catch (error) {
          pieces = [];
        }
        const commodity = await CommodityController.getDetailCommodityByCode(
          pkg.commodityCode
        );
        const localizedCommodityName =
          this.countryCode !== "id"
            ? commodity.commodity_name_en || commodity.commodity_name
            : commodity.commodity_name;
        this.savedPackageDrafts.push({
          key: `sp-${pkg.id}`,
          id: pkg.id,
          isOpen: false,
          commodityCode: pkg.commodityCode,
          commodityName: localizedCommodityName || pkg.commodityName,
          isDangerousGoods: commodity.isDangerousGoods,
          isQuarantine: commodity.isQuarantine,
          totalPieces: pkg.totalPieces,
          totalGrossWeight: pkg.totalGrossWeight,
          pieces
        });
      }
      if (!this.savedPackageDrafts.length) {
        this.addSavedPackage();
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, "Gagal memuat paket tersimpan")
      );
    }
  }

  isDraftValid(draft: SavedPackageDraft): boolean {
    if (!draft.commodityCode) return false;
    if (!draft.pieces.length) return false;
    return draft.pieces.every(
      (p) => p.grossWeight > 0 && p.length > 0 && p.width > 0 && p.height > 0
    );
  }

  get openDraftIsValid(): boolean {
    const openDraft = this.savedPackageDrafts.find((d) => d.isOpen);
    if (!openDraft) return true;
    return this.isDraftValid(openDraft);
  }

  addSavedPackage() {
    if (this.savedPackageDrafts.length >= 3) return;
    if (!this.openDraftIsValid) return;
    const newDraft: SavedPackageDraft = {
      key: `sp-${Date.now()}-${this.savedPackageDrafts.length}`,
      isOpen: true,
      commodityCode: "",
      commodityName: "",
      isDangerousGoods: false,
      isQuarantine: false,
      totalPieces: 1,
      totalGrossWeight: 0,
      pieces: [{ grossWeight: 0, length: 3, width: 20, height: 10 }]
    };
    this.savedPackageDrafts = [
      ...this.savedPackageDrafts.map((d) => ({ ...d, isOpen: false })),
      newDraft
    ];
  }

  async openSavedPackage(key: string) {
    const openDraft = this.savedPackageDrafts.find((d) => d.isOpen);
    if (openDraft && !this.isDraftValid(openDraft)) {
      this.savedPackageDrafts = this.savedPackageDrafts.filter(
        (d) => d.key !== openDraft.key
      );
    }
    const target = this.savedPackageDrafts.find((d) => d.key === key);
    if (target && target.id && !target.pieces.length) {
      try {
        const detail = await CustomerManagementController.getSavedPackageDetail({
          id: this.id,
          packageId: target.id
        });
        const pieces = detail.pieces.map((p) => ({
          grossWeight: p.grossWeight,
          length: p.length,
          width: p.width,
          height: p.height
        }));
        this.savedPackageDrafts = this.savedPackageDrafts.map((d) =>
          d.key === key ? { ...d, pieces } : d
        );
      } catch (error) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, "Gagal memuat paket tersimpan")
        );
        return;
      }
    }
    this.savedPackageDrafts = this.savedPackageDrafts.map((d) => ({
      ...d,
      isOpen: d.key === key
    }));
  }

  removeSavedPackage(key: string) {
    this.savedPackageDrafts = this.savedPackageDrafts.filter(
      (d) => d.key !== key
    );
  }

  get openModalDeleteSavedPackage(): boolean {
    return this.deleteSavedPackageKey !== null;
  }

  openDeleteSavedPackageModal(key: string) {
    this.deleteSavedPackageKey = key;
  }

  handlerDeleteSavedPackage() {
    if (this.deleteSavedPackageKey !== null) {
      this.removeSavedPackage(this.deleteSavedPackageKey);
    }
    this.deleteSavedPackageKey = null;
  }

  onCloseModalDeleteSavedPackage() {
    this.deleteSavedPackageKey = null;
  }

  toRow(draft: SavedPackageDraft) {
    const totalGrossWeight = draft.pieces.length
      ? draft.pieces.reduce((sum, p) => sum + (p.grossWeight || 0), 0)
      : draft.totalGrossWeight;
    return {
      id: draft.id || 0,
      commodityCode: draft.commodityCode,
      commodityName: draft.commodityName,
      totalPieces: draft.pieces.length || draft.totalPieces,
      totalGrossWeight,
      source: "",
      isDangerousGoods: draft.isDangerousGoods,
      isQuarantine: draft.isQuarantine
    };
  }

  get savedPackagesPayload(): SavedPackagePayload[] {
    return this.savedPackageDrafts
      .filter((draft) => draft.commodityCode && draft.pieces.length)
      .map((draft) => {
        const payload: SavedPackagePayload = {
          commodity_code: draft.commodityCode,
          pieces: draft.pieces.map((p) => ({
            gross_weight: p.grossWeight,
            length: p.length,
            width: p.width,
            height: p.height
          }))
        };
        if (draft.id) {
          payload.id = draft.id;
        }
        return payload;
      });
  }

  error = {
    phoneNumber: false,
    phoneNumberCode: false,
    successPhoneNumber: false,
    errorMessagePhoneNumber: false,
    email: false
  };

  // validation name
  name = "";
  validName = true;

  validateName() {
    this.validName = this.name.length !== 0;
  }

  // phone number
  phoneNumber = "";
  phoneNumberCode = "";
  validPhoneNumber = true;

  filterInput(e: any) {
    e.target.value = formatInputNumberOnlyV2(e.target.value);
    this.validatePhoneNumber();
  }

  validatePhoneNumber() {
    this.validPhoneNumber = this.phoneNumber.length !== 0;
  }

  errorPhoneSender = {
    success: false,
    errorPhone: false,
    errorMandatory: false,
    errorPhoneCode: false,
    message: ""
  };

  onvalidatePhoneNumber() {
    return ValidateInputPhoneNumber(
      this.phoneNumberCode?.replace(/\+/, "") + this.phoneNumber
    );
  }
  setPhoneNumber(val: string) {
    this.phoneNumber = val;
    const { error, message } = this.onvalidatePhoneNumber();

    this.errorPhoneSender = {
      ...this.errorPhoneSender,
      success: !this.errorPhoneSender.errorPhoneCode,
      errorPhone: error,
      message
    };
  }
  setPhoneCodeNumber(val: string) {
    this.phoneNumberCode = val;
  }

  setErrorPhoneNumberCode(value: boolean) {
    this.errorPhoneSender = {
      ...this.errorPhoneSender,
      success: !value,
      errorPhoneCode: value
    };
  }

  extractPhoneDetails(number: string, countries: { code: string }[]) {
    const matchingCountry = countries.find((country) =>
      number.startsWith(country.code.replace("+", ""))
    );

    const phoneCode: string = matchingCountry
      ? matchingCountry.code.replace("+", "")
      : "";
    const phoneNumber: string = number.slice(phoneCode.length);

    this.phoneNumberCode = "+" + phoneCode;
    this.phoneNumber = phoneNumber;
  }

  // validation email
  email = "";
  note = "";
  address = "";
  commodity: any = "";
  validAddress = true;
  replaceNewLine(value: string) {
    return removeNewLineWhitespace(value, " ");
  }

  validateAddress() {
    this.validAddress = this.address.length !== 0;
  }

  // error indicator mandatory
  currentFieldIndex = 0;
  setFieldIndex(index: number) {
    if (index > this.currentFieldIndex) {
      this.currentFieldIndex = index;
    }
  }

  get errorMandatory() {
    return [
      !!this.errorName && this.currentFieldIndex > 0,
      !!this.errorPhone && this.currentFieldIndex > 1,
      false, // no used
      false, // no used
      false, // no used
      !!this.errorAddress && this.currentFieldIndex > 5,
      false, // no used
      false, // no used
      false // no used
    ];
  }

  get errorName() {
    if (!this.name) {
      return this.$t("managementCustomer.validation.nameRequired");
    }

    if (this.name.length < 3) {
      return this.$t("managementCustomer.validation.nameMin");
    }

    if (this.name.length > 100) {
      return this.$t("managementCustomer.validation.nameMax");
    }

    return "";
  }

  get errorAddress() {
    if (!this.address) {
      return this.$t("managementCustomer.validation.addressRequired");
    }

    if (this.address.length < 3) {
      return this.$t("managementCustomer.validation.addressMin");
    }

    if (this.address.length > 200) {
      return this.$t("managementCustomer.validation.addressMax");
    }

    return "";
  }

  get errorNotes() {
    if (this.note && this.note.length < 3) {
      return this.$t("managementCustomer.validation.noteMin");
    }

    if (this.note.length > 200) {
      return this.$t("managementCustomer.validation.noteMax");
    }

    return "";
  }

  get errorPhone(): string {
    if (!this.phoneNumber) {
      this.errorPhoneSender.errorMandatory = this.currentFieldIndex > 1;
      return this.$t("managementCustomer.validation.phoneRequired");
    }
    this.errorPhoneSender.errorMandatory = false;

    if (this.errorPhoneSender.errorPhone) {
      return String(this.errorPhoneSender.message);
    }

    if (this.errorPhoneSender.errorPhoneCode) {
      return "error code phone"; // tidak ditampilkan, hanya untuk disable button
    }

    return "";
  }

  // city
  destinationCity: any = "";
  destinationCityList: Array<any> = [];
  onFocusdestinationCity() {
    this.setFieldIndex(7);
    if (!this.destinationCityList.length || !this.destinationCity) {
      LocationController.setLoadingDistrict(true);
      this.fetchdestinationCity("");
    }
  }
  fetchdestinationCity = debounce(async (search: string) => {
    const typeAccuracyAllowed = true;
    const response = await LocationController.getDistrictList({
      search: search,
      status: "",
      page: 1,
      limit: typeAccuracyAllowed ? 50 : 10,
      cache: true
    });
    this.destinationCityList = this.filterDistrict(response);
  }, 250);

  get isLoadingdestinationCity() {
    return LocationController.isLoadingDistrict;
  }

  filterDistrict(response: any) {
    return response
      .filter((key: any) => key.status.toLowerCase() === "active")
      .map((e: any) => ({
        id: e.id,
        name: `${e.name}, ${e.cityName}`,
        code: `${e.originZipCode}`
      }));
  }
  async fetchDistrictId(search: number) {
    if (search) {
      const responseDistrictData = await LocationController.getDetailDistrict({
        id: search,
        notSaveDetail: true
      });
      const disctrictName =
        responseDistrictData.name + "," + responseDistrictData.cityName;
      this.destinationCity =
        responseDistrictData.name == null
          ? ""
          : { id: search, name: disctrictName };
    } else {
      this.destinationCity = "";
    }
  }

  onChangeDestinationCity() {
    if (!this.firstLanding) {
      if (this.destinationCity.code?.includes(",")) {
        const arrayZipcode: string[] = this.destinationCity.code.split(",");
        this.zipCodeList = arrayZipcode.map((e: any) => ({
          code: e,
          name: e
        }));
      } else {
        this.zipCode = this.destinationCity.code
          ? { code: this.destinationCity.code, name: this.destinationCity.code }
          : this.zipCode;
        this.zipCodeList = [
          { code: this.destinationCity.code, name: this.destinationCity.code }
        ];
      }
    }
  }

  //zipcode
  zipCode: any = "";
  zipCodeList: Array<any> = [];

  fetchZipCode = debounce(async (search: string) => {
    if (!this.destinationCity.code) {
      const response = await LocationController.getZipCodeList({
        search: search,
        page: 1,
        limit: 10
      });
      this.zipCodeList = this.filterZipCode(response);
    }
  }, 250);

  filterZipCode(response: any) {
    return response.zipCodeData.map((e: any) => ({
      code: e.originZipCode,
      name: e.originZipCode,
      id: e.id,
      key: `${e.name}, ${e.cityName}`
    }));
  }

  get isLoadingZipCode() {
    return LocationController.isLoadingZipCode;
  }

  onFocusZipCode() {
    this.setFieldIndex(8);
    if (!this.zipCodeList.length || !this.zipCode) {
      this.fetchZipCode("");
    }
  }

  onChangeZipCode() {
    if (!this.destinationCity.code && !this.firstLanding) {
      this.destinationCity = this.zipCode.name
        ? { id: this.zipCode.id, name: this.zipCode.key }
        : "";
    }
  }

  //address type

  addressType = "";
  addressTypeSelect = false;
  get addressTypeData() {
    return [
      {
        name: this.$t("managementCustomer.placeholder.addressType"),
        value: ""
      },
      {
        name: this.$t("managementCustomer.addressTypeHome"),
        value: this.$t("managementCustomer.addressTypeHome").toLowerCase()
      },
      {
        name: this.$t("managementCustomer.addressTypeOffice"),
        value: this.$t("managementCustomer.addressTypeOffice").toLowerCase()
      }
    ];
  }

  onOpenAddressType() {
    this.setFieldIndex(9);
    this.addressTypeSelect = true;
  }

  onCloseAddressType() {
    this.addressTypeSelect = false;
  }

  onSelectAddressType(name: string, value: string) {
    this.addressType = value;
  }

  get isSubmitLoading(): boolean {
    return CustomerManagementController.isSubmitLoading;
  }

  get isErrorForm() {
    return (
      !!this.errorName ||
      !!this.errorPhone ||
      !!this.errorNotes ||
      !!this.errorAddress ||
      this.error.email
    );
  }

  get openModalConfirmation() {
    return CustomerManagementController.openModalConfirmation;
  }

  openConfirmationModal() {
    this.setOpenModalConfirmation(true);
  }

  setOpenModalConfirmation(open: boolean) {
    CustomerManagementController.setOpenModalConfirmation(open);
  }

  handlerSubmit() {
    this.setOpenModalConfirmation(false);
    this._onEdit();
  }

  onCloseModal() {
    this.setOpenModalConfirmation(false);
  }

  openSnackbar(name: string) {
    MainAppController.setSnackbarMessage(
      this.$t("managementCustomer.editSuccess", { name }) as string
    );
    MainAppController.setSnackbar(true);
  }

  // commodity
  dataListCommodities: Commodity[] = [];
  async getCommodityList() {
    const commodities = await CommodityController.getCommodityList(
      new RequestListCommodity({
        page: 1,
        limit: 20,
        commodityStatus: "active"
      })
    );
    this.dataListCommodities = commodities.data;
  }

  fetchCommodityList = debounce(async (search: string) => {
    const commodities = await CommodityController.getCommodityList(
      new RequestListCommodity({
        search: search,
        page: 1,
        limit: 20,
        commodityStatus: "active"
      })
    );
    this.dataListCommodities = commodities.data;
  }, 250);

  async fetchCommodityListInfiniteScroll(search = "") {
    CommodityController.commodities.pagination.page++;
    const commodities = await CommodityController.getCommodityList(
      new RequestListCommodity({
        search,
        page: CommodityController.commodities.pagination.page,
        limit: 20,
        commodityStatus: "active"
      })
    );
    this.dataListCommodities.push(...commodities.data);
  }

  get countryCode() {
    return AccountController.accountData.account_type_detail.countryCode.toLowerCase()
  }

  get commodities() {
    return this.dataListCommodities.map((item) => {
      return {
        id: item.commodity_id,
        name: this.countryCode === "my" ? item.commodity_name_en : item.commodity_name,
        code: item.commodity_code,
        status: item.commodity_status,
        isDangerousGoods: item.isDangerousGoods,
        isQuarantine: item.isQuarantine
      };
    });
  }
  get isLoadingCommodity() {
    return CommodityController.isLoading;
  }

  _onEdit() {
    CustomerManagementController._onEditCustomer({
      id: this.id,
      name: this.name,
      phone: `${this.phoneNumberCode.substring(1)}${this.phoneNumber}`,
      email: this.email,
      address: this.address,
      districtId: Number(this.destinationCity.id),
      zipCode: this.zipCode.name,
      addressType: this.addressType.toLocaleLowerCase(),
      note: this.note,
      commodityCode: this.commodity.code,
      savedPackages: this.savedPackagesPayload
    }).then(() => {
      const customerName = this.name;
      this.$router.push(`/management-customer`).then(() => {
        this.openSnackbar(customerName);
      });
    });
  }

  get openModalDelete() {
    return CustomerManagementController.openModalDelete;
  }

  openDeleteModal() {
    this.setOpenModalDelete(true);
  }

  setOpenModalDelete(open: boolean) {
    CustomerManagementController.setOpenModalDelete(open);
  }

  handlerDelete() {
    this.setOpenModalDelete(false);
    this._onDelete();
  }

  onCloseModalDelete() {
    this.setOpenModalDelete(false);
  }

  async _onDelete() {
    MainAppController.showLoading();
    try {
      await CustomerManagementController.deleteCustomer(this.id);
      const customerName = this.name;
      this.$router.replace(`/management-customer`).then(() => {
        MainAppController.setSnackbarMessage(
          this.$t("managementCustomer.deleteSuccess", {
            name: customerName
          }) as string
        );
        MainAppController.setSnackbarPositivity(false);
        MainAppController.setSnackbar(true);
      });
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("managementCustomer.deleteError") as string,
          () => {
            MainAppController.closeErrorMessage();
            this.openDeleteModal();
          }
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  validateEmail(value: string) {
    this.error.email =
      !!value && !!value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }
}
